import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { ShieldCheck, CheckCircle2, ClipboardCheck, FileText, ChevronLeft, Loader2, Check, Search, CalendarCheck, StickyNote } from 'lucide-react';
import { getBusinessToday, isFutureDate, futureDateClass, FutureDateError } from '../../utils/dateGuards';
import { formatDateOnly } from '../../utils/formatters';
import { autoCommentsCase } from '../../utils/properCase';

// FINAL-REVIEW-001 (Aug 2026) — mandatory pre-submission checklist.
// Mirrors FINAL_REVIEW_ITEMS in backend/routes/search_fulfillment.py.
export const FINAL_REVIEW_ITEMS = [
  { key: 'limited_property_report', label: 'Is this Limited Property Report?', options: ['Yes', 'No', 'NA'] },
  { key: 'ccrs_attached', label: "CC&R's Attached?", options: ['Yes', 'No', 'NA'] },
  { key: 'plat_map_attached', label: 'Plat Map Attached?', options: ['Yes', 'No', 'NA'] },
  { key: 'private_road_agreement', label: 'Is Private Road Agreement Found?', options: ['Yes', 'No', 'NA'] },
  { key: 'names_ran_20_years', label: 'Names Ran for 20 Years?', options: ['Yes', 'No', 'NA'] },
  { key: 'land_records_good_through', label: 'Land Records Good Through Date', kind: 'date' },
  // GA-CHECKLIST-001 (Aug 2026) — mandatory only when property state is GA
  { key: 'ucc_good_through', label: 'UCC Good Through Date', kind: 'date', gaOnly: true, allowNA: true },
  { key: 'liens_good_through', label: 'Liens Good Through Date', kind: 'date', gaOnly: true, allowNA: true },
  { key: 'ucc_search_performed', label: 'UCC Search Performed?', options: ['Yes', 'No', 'NA'] },
  { key: 'misc_documents_found', label: 'Miscellaneous Documents Found?', options: ['Yes', 'No', 'NA'] },
  // REQUIRED-AMOUNT-001 (Jun 2026): "Required Amount $25,000.00 Mortgage?"
  // retired — Required Amount now flows from the qualifying mortgage selected
  // in the Mortgage section (read-only row rendered in Search Verification).
  { key: 'abstract_property', label: 'Abstract Property?', options: ['Yes', 'No', 'NA'] },
];

// FINAL-REVIEW-002 (Aug 2026) — logical grouping for the redesigned checklist
export const CHECKLIST_GROUPS = [
  {
    title: 'Property & Search', icon: Search,
    keys: ['limited_property_report', 'ccrs_attached', 'plat_map_attached', 'private_road_agreement', 'names_ran_20_years'],
  },
  {
    title: 'Good Through Dates', icon: CalendarCheck,
    keys: ['land_records_good_through', 'ucc_good_through', 'liens_good_through'],
  },
  {
    title: 'Search Verification', icon: ShieldCheck,
    keys: ['ucc_search_performed', 'misc_documents_found', 'abstract_property'],
  },
];
export const ITEM_BY_KEY = Object.fromEntries(FINAL_REVIEW_ITEMS.map(i => [i.key, i]));

export const OPT_SELECTED = {
  Yes: 'bg-emerald-600 border-emerald-600 text-white shadow-sm',
  No: 'bg-rose-600 border-rose-600 text-white shadow-sm',
  NA: 'bg-slate-600 border-slate-600 text-white shadow-sm',
};

export const emptyChecklist = () => Object.fromEntries(FINAL_REVIEW_ITEMS.map(i => [i.key, '']));

export const isGAState = (s) => ['GA', 'GEORGIA'].includes((s || '').trim().toUpperCase());

export const itemAnswered = (item, value, isGA) => {
  const v = (value || '').trim();
  if (item.kind === 'date') {
    if (!v) return !!item.gaOnly && !isGA; // optional outside Georgia
    if (v === 'NA') return !!item.allowNA; // FINAL-REVIEW-UX-001
    return !isFutureDate(v);
  }
  return item.options.includes(v);
};

export const isItemRequired = (item, isGA) => !(item.gaOnly && !isGA);

// REQUIRED-AMOUNT-001 — derives the Required Amount from the qualifying
// mortgage selected in the Mortgage section ("None" when nothing selected).
export const requiredAmountFromData = (data) => {
  const idx = (data?.dot_mortgage_information || []).findIndex(m => m.use_for_required_amount === true);
  if (idx < 0) return { value: 'None', mortgageNumber: null };
  const amt = ((data.dot_mortgage_information[idx].original_amount || '') + '').trim();
  return { value: amt || 'None', mortgageNumber: idx + 1 };
};

const Row = ({ label, value }) => (
  (value || '').trim() ? (
    <div className="flex gap-2 text-[12px]">
      <span className="text-slate-400 min-w-[130px] flex-shrink-0">{label}</span>
      <span className="text-slate-700 font-medium whitespace-pre-wrap break-words">{value}</span>
    </div>
  ) : null
);

const ReportSection = ({ title, children }) => (
  <div className="border border-slate-200 rounded-lg p-3 bg-white">
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">{title}</p>
    <div className="space-y-1">{children}</div>
  </div>
);

// Read-only rendering of the exact fulfillment payload being submitted.
const ReportReview = ({ data, checklist }) => {
  const parcels = data.parcel_ids.filter(p => (p.value || '').trim());
  const deeds = data.deeds.filter(d => d.grantor || d.grantee || d.deed_date || d.recorded_date || d.book || d.page || d.instrument_number || d.document_type);
  const mortgages = data.dot_mortgage_information.filter(m => Object.entries(m).some(([k, v]) => k !== 'id' && (Array.isArray(v) ? v.length && v.some(s => Object.entries(s).some(([sk, sv]) => sk !== 'id' && (sv || '').toString().trim())) : (v || '').toString().trim())));
  const liens = data.legal_actions.filter(la => Object.entries(la).some(([k, v]) => k !== 'id' && (v || '').trim()));
  return (
    <div className="space-y-2.5" data-testid="final-review-report">
      <ReportSection title="Search Setup">
        <Row label="Parcel ID(s)" value={parcels.map(p => p.value.trim()).join(', ')} />
        <Row label="Effective Date" value={data.effective_date} />
        <Row label="Effective Time" value={data.effective_time} />
      </ReportSection>
      {parcels.some((p, i) => Object.entries(data.tax_summaries[i] || {}).some(([k, v]) => k !== 'id' && String(v ?? '').trim() && String(v) !== 'false')) && (
        <ReportSection title="Tax Summary">
          {parcels.map((p, i) => {
            const ts = data.tax_summaries[i] || {};
            return (
              <div key={p.id} className="text-[12px] text-slate-700">
                <span className="font-semibold">Parcel #{i + 1} ({p.value.trim()}):</span>{' '}
                {ts.assessed_values_na === true
                  ? <span className="font-semibold text-amber-700">Assessed Values not available!</span>
                  : <>Land {ts.land_value || '—'} · Improvement {ts.improvement_value || '—'} · Total {ts.total_assessed_value || '—'}</>}
                {(ts.notes || '').trim() ? <span className="text-slate-500"> — {ts.notes}</span> : null}
              </div>
            );
          })}
        </ReportSection>
      )}
      <ReportSection title={`Deeds / Chain of Title (${deeds.length})`}>
        {deeds.length === 0 && <p className="text-[12px] text-slate-400 italic">No deed entries</p>}
        {deeds.map((d, i) => (
          <div key={d.id || i} className="text-[12px] text-slate-700">
            <span className="font-semibold">#{i + 1}</span> {d.grantor || '—'} → {d.grantee || '—'}
            {d.deed_date ? ` · Dated ${formatDateOnly(d.deed_date)}` : ''}{d.recorded_date ? ` · Recorded ${formatDateOnly(d.recorded_date)}` : ''}
            {d.document_type ? ` · ${d.document_type}` : ''}{d.consideration_amount ? ` · ${d.consideration_amount}` : ''}
          </div>
        ))}
      </ReportSection>
      <ReportSection title="Vesting & Legal">
        <Row label="Vesting" value={data.vesting_info} />
        <Row label="Estate Type" value={data.estate_type} />
        <Row label="Legal Description" value={data.legal_description} />
      </ReportSection>
      <ReportSection title={`Mortgage / DOT (${data.no_open_mortgage ? 'No Open Mortgage' : mortgages.length})`}>
        {data.no_open_mortgage ? (
          <p className="text-[12px] font-semibold text-emerald-700" data-testid="final-review-no-open-mortgage">
            No Open Mortgage — confirmed by processor. Mortgage detail fields were disabled for this order.
          </p>
        ) : (
          <>
            {mortgages.length === 0 && <p className="text-[12px] text-slate-400 italic">No open mortgages / deeds of trust</p>}
            {mortgages.map((m, i) => (
          <div key={m.id || i} className="text-[12px] text-slate-700">
            <span className="font-semibold">#{i + 1}</span> {m.type || 'Mortgage'} · {m.lender || '—'} / {m.borrower || '—'}
            {m.original_amount ? ` · ${m.original_amount}` : ''}{m.dated_date ? ` · Dated ${formatDateOnly(m.dated_date)}` : ''}
            {(m.assignments || []).some(a => Object.entries(a).some(([k, v]) => k !== 'id' && (v || '').trim())) ? ` · ${m.assignments.length} assignment(s)` : ''}
            {(m.modifications || []).some(a => Object.entries(a).some(([k, v]) => k !== 'id' && (v || '').trim())) ? ` · ${m.modifications.length} modification(s)` : ''}
            {(m.appointed_trustees || []).some(a => Object.entries(a).some(([k, v]) => k !== 'id' && (v || '').trim())) ? ` · ${m.appointed_trustees.length} appointed trustee(s)` : ''}
            {(m.subordination_agreements || []).some(a => Object.entries(a).some(([k, v]) => k !== 'id' && (v || '').trim())) ? ` · ${m.subordination_agreements.length} subordination(s)` : ''}
            {(m.notices_of_pendency || []).some(a => Object.entries(a).some(([k, v]) => k !== 'id' && (v || '').trim())) ? ` · ${m.notices_of_pendency.length} notice(s) of pendency` : ''}
          </div>
            ))}
          </>
        )}
        {(data.dot_mortgage_information || []).some(m => (m.comments || '').trim()) && (
          <Row label="Comments / Notes" value={(data.dot_mortgage_information || []).map(m => (m.comments || '').trim()).filter(Boolean).join('\n')} />
        )}
      </ReportSection>
      <ReportSection title={`Judgment / Lien Types (${liens.length})`}>
        {liens.length === 0 && <p className="text-[12px] text-slate-400 italic">No judgments / liens</p>}
        {liens.map((la, i) => (
          <div key={la.id || i} className="text-[12px] text-slate-700">
            <span className="font-semibold">#{i + 1}</span> {la.plaintiff || '—'} v. {la.defendant || '—'}
            {la.judgment_type ? ` · ${la.judgment_type}` : ''}{la.judgment_amount ? ` · ${la.judgment_amount}` : ''}
            {la.recorded_date ? ` · Recorded ${formatDateOnly(la.recorded_date)}` : ''}
          </div>
        ))}
      </ReportSection>
      {/* NAMES-RUN-001 — names searched during the title search */}
      {(() => {
        const namesRun = (data.names_run || []).map(n => ((n && n.value) || '').trim()).filter(Boolean);
        if (!namesRun.length) return null;
        return (
          <ReportSection title={`Names Run (${namesRun.length})`}>
            {namesRun.map((n, i) => (
              <p key={i} className="text-[12px] text-slate-700" data-testid={`final-review-names-run-${i}`}>{n}</p>
            ))}
          </ReportSection>
        );
      })()}
      <ReportSection title="Primary Search Document">
        <Row label="Document" value={data.primary_document_name || '(not selected)'} />
      </ReportSection>
      {/* FINAL-REVIEW-PDF-001 — mirrors the PDF's Additional Comments / Information section */}
      {(Object.values(checklist || {}).some(v => (v || '').trim()) || (data.misc_notes || '').trim()) ? (
        <ReportSection title="Additional Comments / Information">
          {FINAL_REVIEW_ITEMS.map(item => {
            const v = ((checklist || {})[item.key] || '').trim();
            if (!v) return null;
            const val = item.options ? (v === 'NA' ? 'N/A' : v.toUpperCase())
              : (/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v.slice(5, 7)}/${v.slice(8, 10)}/${v.slice(0, 4)}` : v);
            return (
              <p key={item.key} className="text-[12px] text-slate-700" data-testid={`final-review-report-checklist-${item.key}`}>
                <span className="font-semibold">{item.label.replace(/\?$/, '').toUpperCase()}:</span> {val}
              </p>
            );
          })}
          {(() => {
            const ra = requiredAmountFromData(data);
            return (
              <p className="text-[12px] text-slate-700" data-testid="final-review-report-required-amount">
                <span className="font-semibold">REQUIRED AMOUNT:</span> {ra.value}
                {ra.mortgageNumber ? <span className="text-slate-400"> (Mortgage #{ra.mortgageNumber})</span> : null}
              </p>
            );
          })()}
          {(data.misc_notes || '').trim() ? (
            <p className="text-[12px] text-slate-700 whitespace-pre-wrap break-words mt-1.5" data-testid="final-review-report-misc-notes">
              {data.misc_notes}
            </p>
          ) : null}
        </ReportSection>
      ) : null}
    </div>
  );
};

export default function FinalReviewModal({ open, actionType, initialChecklist, data, propertyState, submitting, onMiscNotesChange, onCancel, onConfirm }) {
  const [step, setStep] = useState(1);
  const [checklist, setChecklist] = useState(emptyChecklist());
  const [ackChecklist, setAckChecklist] = useState(false);
  const [ackReport, setAckReport] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [flashKey, setFlashKey] = useState(null);
  const itemRefs = useRef({});
  const ackRef = useRef(null);
  const flashTimer = useRef(null);
  const isGA = isGAState(propertyState);

  useEffect(() => {
    if (open) {
      setStep(1);
      setChecklist({ ...emptyChecklist(), ...(initialChecklist || {}) });
      // Acknowledgments must be re-given on every review — never pre-checked.
      setAckChecklist(false);
      setAckReport(false);
      setAttempted(false);
      setFlashKey(null);
    }
  }, [open, initialChecklist]);

  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  const requiredItems = FINAL_REVIEW_ITEMS.filter(i => isItemRequired(i, isGA));
  const answeredCount = requiredItems.filter(i => itemAnswered(i, checklist[i.key], isGA) && (checklist[i.key] || '').trim()).length;
  const allAnswered = FINAL_REVIEW_ITEMS.every(item => itemAnswered(item, checklist[item.key], isGA));
  const progressPct = requiredItems.length ? Math.round((answeredCount / requiredItems.length) * 100) : 100;
  const actionLabel = actionType === 'qc_review' ? 'Send to QC Review' : 'Complete Order';

  const flash = (key, el) => {
    setFlashKey(key);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashKey(null), 1800);
  };

  const handleContinue = () => {
    const firstMissing = FINAL_REVIEW_ITEMS.find(i => isItemRequired(i, isGA) && !itemAnswered(i, checklist[i.key], isGA));
    if (firstMissing) {
      setAttempted(true);
      const el = itemRefs.current[firstMissing.key];
      flash(firstMissing.key, el);
      if (firstMissing.kind === 'date') el?.querySelector('input[type="date"]')?.focus();
      return;
    }
    if (!ackChecklist) {
      setAttempted(true);
      flash('__ack__', ackRef.current);
      return;
    }
    setStep(2);
  };

  const renderItem = (item) => {
    const idx = FINAL_REVIEW_ITEMS.indexOf(item);
    const val = checklist[item.key] || '';
    const required = isItemRequired(item, isGA);
    const answered = itemAnswered(item, val, isGA);
    const missing = required && !answered;
    const flashing = flashKey === item.key;
    return (
      <div
        key={item.key}
        ref={el => { itemRefs.current[item.key] = el; }}
        className={`flex items-center justify-between gap-3 px-3 py-2.5 transition-colors ${
          flashing ? 'bg-amber-100 ring-2 ring-inset ring-amber-400' :
          missing ? (attempted ? 'bg-amber-50 border-l-2 border-amber-400' : 'bg-amber-50/40') : 'bg-white'
        }`}
        data-testid={`final-review-item-${item.key}`}
      >
        <span className="text-[12.5px] font-medium text-slate-700 flex items-center flex-wrap gap-x-1.5">
          <span className={`h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${
            answered && (val || '').trim() ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
          }`}>
            {answered && (val || '').trim() ? <Check className="h-3 w-3" /> : idx + 1}
          </span>
          {item.label}
          {item.gaOnly && !isGA && (
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide" data-testid={`final-review-optional-tag-${item.key}`}>(Optional)</span>
          )}
          {item.gaOnly && isGA && (
            <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide" data-testid={`final-review-ga-required-tag-${item.key}`}>(GA — Required)</span>
          )}
          {missing && attempted && (
            <span className="text-[9.5px] font-bold text-amber-600 uppercase tracking-wide" data-testid={`final-review-missing-tag-${item.key}`}>Required</span>
          )}
        </span>
        {item.kind === 'date' ? (
          <div className="flex-shrink-0 flex items-center gap-1.5">
            <div>
              <input
                type="date"
                max={getBusinessToday()}
                value={val === 'NA' ? '' : val}
                disabled={val === 'NA'}
                onChange={e => setChecklist(prev => ({ ...prev, [item.key]: e.target.value }))}
                className={`h-8 text-[12px] border rounded-md px-2 focus:outline-none focus:border-blue-400 ${val === 'NA' ? 'opacity-40' : ''} ${missing && attempted ? 'border-amber-400' : 'border-slate-200'}${futureDateClass(val === 'NA' ? '' : val)}`}
                data-testid={`final-review-date-${item.key}`}
              />
              <FutureDateError value={val === 'NA' ? '' : val} />
            </div>
            {item.allowNA && (
              <button
                type="button"
                onClick={() => setChecklist(prev => ({ ...prev, [item.key]: prev[item.key] === 'NA' ? '' : 'NA' }))}
                className={`flex items-center gap-1 px-3 py-1.5 text-[11.5px] font-semibold rounded-full border transition-colors ${
                  val === 'NA' ? OPT_SELECTED.NA : 'bg-white border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-700'
                }`}
                aria-pressed={val === 'NA'}
                data-testid={`final-review-${item.key}-na`}
              >
                {val === 'NA' && <Check className="h-3 w-3" />}
                NA
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {item.options.map(opt => {
              const selected = val === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setChecklist(prev => ({ ...prev, [item.key]: opt }))}
                  className={`flex items-center gap-1 px-3 py-1.5 text-[11.5px] font-semibold rounded-full border transition-colors ${
                    selected
                      ? OPT_SELECTED[opt] || OPT_SELECTED.NA
                      : 'bg-white border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-700'
                  }`}
                  aria-pressed={selected}
                  data-testid={`final-review-${item.key}-${opt.toLowerCase()}`}
                >
                  {selected && <Check className="h-3 w-3" />}
                  {opt}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onCancel(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col" aria-describedby={undefined} data-testid="final-review-modal">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-blue-50 flex items-center justify-center">
              {step === 1 ? <ClipboardCheck className="h-4 w-4 text-blue-600" /> : <FileText className="h-4 w-4 text-blue-600" />}
            </span>
            {step === 1 ? 'Final Review — Mandatory Checklist' : 'Final Review — Report Review'}
            <span className="ml-auto text-[11px] font-medium text-slate-400">Step {step} of 2</span>
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <>
            {/* FINAL-REVIEW-002 — completion status */}
            <div className="-mt-1">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[12px] font-semibold text-slate-600" data-testid="final-review-progress">
                  {answeredCount} of {requiredItems.length} checks completed
                </p>
                <p className="text-[11px] text-slate-400">Every required item must be answered before continuing.</p>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${progressPct === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                  style={{ width: `${progressPct}%` }}
                  data-testid="final-review-progress-bar"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto pr-1 space-y-3" data-testid="final-review-checklist">
              {CHECKLIST_GROUPS.map(group => {
                const items = group.keys.map(k => ITEM_BY_KEY[k]).filter(Boolean);
                const groupRequired = items.filter(i => isItemRequired(i, isGA));
                const groupAnswered = groupRequired.filter(i => itemAnswered(i, checklist[i.key], isGA) && (checklist[i.key] || '').trim()).length;
                const GroupIcon = group.icon;
                return (
                  <div key={group.title} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`final-review-group-${group.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100">
                      <GroupIcon className="h-3.5 w-3.5 text-blue-600" />
                      <p className="text-[10.5px] font-bold text-slate-500 uppercase tracking-wider">{group.title}</p>
                      <span className={`ml-auto text-[10.5px] font-semibold ${groupAnswered === groupRequired.length ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {groupAnswered}/{groupRequired.length}
                      </span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {items.map(renderItem)}
                      {/* REQUIRED-AMOUNT-001 — auto-resolved from the Mortgage section */}
                      {group.title === 'Search Verification' && (() => {
                        const ra = requiredAmountFromData(data);
                        return (
                          <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-blue-50/40" data-testid="final-review-required-amount-row">
                            <span className="text-[12.5px] font-medium text-slate-700 flex items-center flex-wrap gap-x-1.5">
                              <span className={`h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 ${ra.mortgageNumber ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                                <Check className="h-3 w-3" />
                              </span>
                              Required Amount
                              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">(auto — from Mortgage section)</span>
                            </span>
                            <span className={`text-[12px] font-bold flex-shrink-0 ${ra.mortgageNumber ? 'text-emerald-700' : 'text-slate-500'}`} data-testid="final-review-required-amount-value">
                              {ra.value}{ra.mortgageNumber ? ` · Mortgage #${ra.mortgageNumber}` : ''}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}

              {/* MISC-NOTES-001 — free-form report notes (optional, never normalized) */}
              <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="final-review-group-additional-notes">
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100">
                  <StickyNote className="h-3.5 w-3.5 text-blue-600" />
                  <p className="text-[10.5px] font-bold text-slate-500 uppercase tracking-wider">Miscellaneous / Additional Notes</p>
                  <span className="ml-auto text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Optional</span>
                </div>
                <div className="p-3 bg-white">
                  <textarea
                    rows={4}
                    className="w-full text-[12.5px] border border-slate-200 rounded-lg px-3 py-2 resize-y min-h-[90px] focus:outline-none focus:border-blue-400 placeholder:text-slate-400"
                    placeholder="Enter any additional findings, exceptions, observations, or report notes..."
                    value={data.misc_notes || ''}
                    onChange={e => onMiscNotesChange?.(e.target.value)}
                    onBlur={e => { const v = autoCommentsCase(e.target.value); if (v !== e.target.value) onMiscNotesChange?.(v); }}
                    data-testid="final-review-misc-notes"
                  />
                  <p className="text-[10.5px] text-slate-400 mt-1">
                    Saved with the fulfillment data — shown in the final Search Report only when filled in. ALL-CAPS text is converted to readable casing when you leave the field; mixed-case text is kept exactly as entered.
                  </p>
                </div>
              </div>
            </div>
            <label
              ref={ackRef}
              className={`flex items-start gap-2.5 border rounded-lg px-3 py-2.5 transition-colors ${
                flashKey === '__ack__' ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-300' : 'border-slate-200 bg-slate-50/60'
              } ${allAnswered ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
            >
              <input
                type="checkbox"
                checked={ackChecklist}
                disabled={!allAnswered}
                onChange={e => setAckChecklist(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-blue-600 disabled:cursor-not-allowed"
                data-testid="final-review-checklist-ack"
              />
              <span className="text-[12px] text-slate-600">
                I acknowledge that I have reviewed and completed this checklist accurately for this order.
                {!allAnswered && (
                  <span className="block text-[11px] text-amber-600 mt-0.5" data-testid="final-review-ack-locked-hint">
                    Answer all required checks above to enable this acknowledgement.
                  </span>
                )}
              </span>
            </label>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onCancel} data-testid="final-review-cancel-btn">Cancel</Button>
              <Button
                size="sm"
                className={`bg-blue-600 hover:bg-blue-700 text-white ${(!allAnswered || !ackChecklist) ? 'opacity-50' : ''}`}
                data-incomplete={!allAnswered || !ackChecklist ? 'true' : 'false'}
                onClick={handleContinue}
                data-testid="final-review-continue-btn"
              >
                Continue to Report Review
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-slate-500 -mt-1">
              This is the exact fulfillment data that will be submitted with this order. Review it carefully before {actionType === 'qc_review' ? 'sending to QC Review' : 'completing'}.
            </p>
            <div className="flex-1 overflow-y-auto pr-1">
              <ReportReview data={data} checklist={checklist} />
            </div>
            <label className="flex items-start gap-2.5 border border-slate-200 rounded-lg px-3 py-2.5 bg-slate-50/60 cursor-pointer">
              <input
                type="checkbox"
                checked={ackReport}
                onChange={e => setAckReport(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-blue-600"
                data-testid="final-review-report-ack"
              />
              <span className="text-[12px] text-slate-600">
                I acknowledge that I have reviewed the final report above and it is accurate and complete.
              </span>
            </label>
            <div className="flex items-center justify-between gap-2">
              <Button variant="outline" size="sm" onClick={() => setStep(1)} disabled={submitting} data-testid="final-review-back-btn">
                <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back to Checklist
              </Button>
              <Button
                size="sm"
                className={actionType === 'qc_review' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                disabled={!ackReport || submitting}
                onClick={() => onConfirm(checklist)}
                data-testid="final-review-submit-btn"
              >
                {submitting
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : actionType === 'qc_review' ? <ShieldCheck className="h-3.5 w-3.5 mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                {actionLabel}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
